import {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import debounce from 'lodash/debounce';

import {ApiStatus} from '../../../shared/models';
import {updateReport} from '../../../shared/store/actions/reportFormActions';
import {IState} from '../../../shared/store/reducers';
import {toReportEntity} from '../../../shared/utils/report';
import {AUTOSAVE_TIME} from '../constants';

export function useAutosaveReport(
  uid: string,
): void {
  const pending = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const report = useSelector((state: IState) => state.reportForm.report);
  const reportBlocks = useSelector((state: IState) => state.reportForm.blocks);
  const isDirty = useSelector((state: IState) => state.reportForm.isDirty);
  const instant = useSelector((state: IState) => state.reportForm.instant);
  const updatingStatus = useSelector((state: IState) => state.reportForm.updatingStatus);
  const dispatch = useDispatch();
  const isUpdating = updatingStatus === ApiStatus.LOADING;

  /*
   * Make sure to trigger update only if new blocks were added
   */
  const reportBlocksIds = useMemo(
    () => reportBlocks?.filter((b: any) => !!b.block_id)?.map((b: any) => b.block_id),
    [reportBlocks]);

  useEffect(() => {
    return () => {
      pending.current = false;
      if (debounceUpdateReportFn && debounceUpdateReportFn.hasOwnProperty('cancel')) {
        debounceUpdateReportFn.cancel();
      }
    };
  }, []);

  const updateReportFn = useCallback((uid: string, newReport: any, force: boolean = false) => {
    if (!force && !pending.current) {
      /*
       * If `pending` is false and this function managed to be called without `force` set to true,
       *   it means it was canceled by an instant call.
       * The instant call was triggered afterward,
       *   meaning that it had the latest value that this outdated debounced call.
       */
      return;
    }
    pending.current = false;

    dispatch(updateReport(uid, newReport));
    setIsSaving(true);
  }, []);
  /*
   * Only call the last execution
   */
  const debounceUpdateReportFn = useMemo(() => debounce(updateReportFn, AUTOSAVE_TIME), []);

  useEffect(() => {
    /*
     * If updating finished, set isSaving to false
     */
    if (isSaving && !isUpdating) {
      setIsSaving(false);
    }
  }, [isSaving, isUpdating]);

  useEffect(() => {
    if (!isDirty || !report) {
      return;
    }
    const newReport = toReportEntity(report as any, reportBlocks as any || []);

    if (instant) {
      /*
       * In case there is a pending request, unset it as we're about to update the report instantly
       */
      pending.current = false;
      /*
       * Don't use debounce, update immediately
       */
      updateReportFn(uid, newReport, true);
    } else {
      pending.current = true;
      debounceUpdateReportFn(uid, newReport);
    }
  }, [report, isDirty, reportBlocksIds?.length]);
}
